import { CommonModule } from '@angular/common';
import { Component, inject, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActivityAssociationInterface,
  ActivityInterface,
  CRMActivityApiService,
  FieldValueInterface,
  GetMultiActivityResponse,
  FieldMaskInterface,
} from '@vendasta/crm';
import {
  ActivityTaskStatus,
  ActivityType,
  CrmCompanyAssociationFormFieldComponent,
  CrmContactToCompanyAssociationFormFieldComponent,
  CrmActivityLoggerTaskComponent,
  CrmDrawerComponent,
  CrmFieldService,
  CrmInjectionToken,
  CRMRowObject,
  CustomRowStyle,
  ListObjectsPageComponent,
  ListObjectsTableContentDirective,
  MultiRowAction,
  ObjectAssociationType,
  ObjectType,
  SimplifiedUser,
  SingleRowAction,
  StandardIds,
  UserWithFullName,
  FormFieldAssociation,
  CrmMultilocationInjectionToken,
  CrmObjectManagementTableChangesService,
} from '@galaxy/crm/static';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CustomCell, Row } from '@vendasta/galaxy/table';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { ObservableWorkState } from '@vendasta/rx-utils/work-state';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  tap,
  ReplaySubject,
  shareReplay,
  switchMap,
  withLatestFrom,
  BehaviorSubject,
  debounceTime,
  take,
  combineLatest,
} from 'rxjs';
import { CrmTaskQueueComponent } from '../../crm-components/task-queue';
import { HttpErrorResponse } from '@angular/common/http';
import { PermissionRequiredPanelComponent } from '../../crm-components/permission-required';
import { TaskQueueService } from '../../crm-components/task-queue/task-queue.service';

const errorTextColor = '#c62828'; //galaxy light mode $error-text-color

type RequestType = 'create' | 'edit';

interface TaskResponse {
  crmObjectId: string;
  request: RequestType;
}

interface TaskWithNamespace {
  id: string;
  namespace: string;
}

const manuallyLoggedFieldValue: FieldValueInterface = {
  fieldId: StandardIds.ActivityManuallyLogged,
  booleanValue: true,
};

enum TaskErrorStatus {
  NO_ERROR = 0,
  UNKNOWN_ERROR = 1,
  ACCESS_DENIED = 2,
}

@Component({
  selector: 'crm-list-tasks',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  imports: [
    ListObjectsPageComponent,
    CommonModule,
    CrmDrawerComponent,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    CrmActivityLoggerTaskComponent,
    ListObjectsTableContentDirective,
    TranslateModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyFormFieldModule,
    CrmContactToCompanyAssociationFormFieldComponent,
    CrmCompanyAssociationFormFieldComponent,
    CrmTaskQueueComponent,
    MatIconModule,
    PermissionRequiredPanelComponent,
  ],
})
export class ListTasksPageComponent {
  @ViewChild('drawer') drawer?: CrmDrawerComponent;
  @ViewChild('upsert') upsertTemplateRef?: TemplateRef<any>;
  @ViewChild(CrmActivityLoggerTaskComponent) taskLogger?: CrmActivityLoggerTaskComponent;
  @ViewChild(CrmTaskQueueComponent) taskQueue?: CrmTaskQueueComponent;

  private readonly activityService = inject(CRMActivityApiService);
  private readonly fieldService = inject(CrmFieldService);
  private readonly config = inject(CrmInjectionToken);
  private readonly multiLocationConfig = inject(CrmMultilocationInjectionToken);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly taskQueueService = inject(TaskQueueService);
  private readonly crmObjectManagementTableChangesService = inject(CrmObjectManagementTableChangesService);
  private readonly dialog = inject(MatDialog);

  protected readonly isMultiLocation$ = this.multiLocationConfig?.isMultiLocation$ ?? of(false);
  protected isMultiLocation = toSignal(this.isMultiLocation$);
  protected contactAssociations: FormFieldAssociation[] = [];
  protected companyAssociations: FormFieldAssociation[] = [];
  protected otherAssociations: FormFieldAssociation[] = [];
  protected readonly taskWorkState = new ObservableWorkState<TaskResponse>();
  protected readonly tableRows = signal<Row[]>([]);
  protected readonly presetFilters$ = this.config.task?.presetFilters$ ?? of([]);
  private refreshRow$$ = new BehaviorSubject<string>('');
  protected readonly refreshRow$ = this.refreshRow$$.asObservable();

  protected readonly customRowStyle: CustomRowStyle = (row: Row) => {
    if (this.isComplete(row))
      return {
        'text-decoration': 'line-through',
      };
    if (this.isOverdue(row))
      return {
        color: errorTextColor,
      };
    return {};
  };
  protected readonly objectType: ObjectType = 'Activity';
  protected readonly activityType: ActivityType = 'Task';

  private readonly currentRowNamespace$$ = new BehaviorSubject<string>('');
  private readonly activityId$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private readonly taskPermissionsSubject$$ = new BehaviorSubject<TaskWithNamespace[]>([]);
  protected readonly taskPermissions$ = this.taskPermissionsSubject$$.pipe(
    switchMap((tasks) => this.verifyTaskPermissions(tasks)),
    shareReplay(1),
  );
  protected activity$: Observable<ActivityInterface>;
  protected TaskError = TaskErrorStatus;
  protected activityLoadError$: Observable<TaskErrorStatus>;

  protected readonly activityOwner$: Observable<UserWithFullName | null>;
  protected readonly user$ = this.config.currentUserId$.pipe(
    switchMap((userId) => this.config.services?.userService?.getMultiUsers([userId]) ?? of([])),
    map((users) => this.mapUser(users?.[0])),
  );

  protected readonly hasAccessToFeature$ = this.config.hasAccessToFeature$?.('task-queue') ?? of(false);

  protected readonly multiRowActions$: Observable<MultiRowAction[]> = this.hasAccessToFeature$.pipe(
    map((hasAccess) => {
      const actions: MultiRowAction[] = [
        {
          label: 'TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.PLURAL.TITLE',
          callback: (rows: Row[]) => {
            const validRows = rows.filter((row) => !this.isComplete(row) && row.id);

            if (validRows.length === 0) {
              this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.TASK_QUEUE.NO_TASKS_SELECTED');
              return;
            }

            const tasksToComplete: TaskWithNamespace[] = validRows.map((row) => {
              const namespace = row.data['namespace']?.value ?? '';
              return {
                id: row.id,
                namespace,
              };
            });

            firstValueFrom(this.completeTaskMulti(tasksToComplete)).then((failedIDs) => {
              const amountFailed = failedIDs.length;
              if (amountFailed == 0) {
                this.snackbarService.openSuccessSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.PLURAL.SUCCESS');
              } else {
                console.error('Failed to complete Task IDs: ', failedIDs);
                this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.PLURAL.ERROR', {
                  interpolateTranslateParams: { amount: `${amountFailed}`, total: `${tasksToComplete.length}` },
                });
              }
            });

            this.analyticsService.trackEvent('task-table-complete-multi', 'sales-tasks', 'click', 1, {
              numTasks: tasksToComplete.length,
            });
          },
        },
      ];
      if (hasAccess) {
        actions.push({
          label: 'TASK.LIST_OBJECTS_TABLE.ACTIONS.TASK_QUEUE.START_TASKS',
          callback: (rows: Row[]) => {
            const validRows = rows.filter((row) => row.id);

            const tasks: TaskWithNamespace[] = validRows.map((row) => {
              const namespace = row.data['namespace']?.value ?? '';
              return {
                id: row.id,
                namespace,
              };
            });

            this.startTaskQueue(tasks.map((task) => task.id));
          },
        });
      }
      return actions;
    }),
  );
  protected readonly singleRowActions$: Observable<SingleRowAction[]> = this.hasAccessToFeature$.pipe(
    map((hasAccess) => {
      const actions: SingleRowAction[] = [
        {
          label: 'TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.TITLE',
          visible: (row: Row) => !this.isComplete(row),
          callback: (row) => {
            const namespace = row.data['namespace']?.value ?? '';

            const task: TaskWithNamespace = {
              id: row.id,
              namespace,
            };

            this.completeTask(task).subscribe({
              next: (success) => {
                if (success) {
                  this.snackbarService.openSuccessSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.SUCCESS');
                } else {
                  console.error('Failed to complete Task with ID: ', row.id);
                  this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.ERROR');
                }
              },
              error: (err) => {
                console.error('Error completing task:', err);
                this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.ERROR');
              },
            });

            this.analyticsService.trackEvent('task-table-complete-single', 'sales-tasks', 'click', 1);
          },
        },
      ];
      if (hasAccess) {
        actions.push({
          label: 'TASK.LIST_OBJECTS_TABLE.ACTIONS.TASK_QUEUE.START_TASK',
          callback: (row) => {
            const namespace = row.data['namespace']?.value ?? '';

            const task: TaskWithNamespace = {
              id: row.id,
              namespace,
            };

            this.taskPermissionsSubject$$.next([task]);
            firstValueFrom(combineLatest([this.isMultiLocation$, this.taskPermissions$])).then(
              ([isMultiLocation, results]) => {
                const taskResult =
                  Array.isArray(results) && results.length > 0
                    ? results.find((result) => result.id === task.id)
                    : undefined;
                const hasAccess = taskResult?.hasAccess ?? false;

                if (isMultiLocation) {
                  if (hasAccess) {
                    const taskNamespaces = new Map<string, string>();
                    taskNamespaces.set(task.id, task.namespace);
                    this.taskQueueService.setTaskNamespaces(taskNamespaces);
                    this.taskQueueService.startQueue([task.id]);
                    this.analyticsService.trackEvent('task-table-start-single', 'sales-tasks', 'click', 1);
                  } else {
                    this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.ERROR');
                  }
                  this.taskPermissionsSubject$$.next([]);
                } else {
                  this.taskQueueService.startQueue([task.id], task.namespace);
                  this.analyticsService.trackEvent('task-table-start-single', 'sales-tasks', 'click', 1);
                  this.taskPermissionsSubject$$.next([]);
                }
              },
            );
          },
        });
      }
      return actions;
    }),
  );
  constructor() {
    this.taskWorkState.successEvents$
      .pipe(
        takeUntilDestroyed(),
        switchMap((valid) => (valid ? this.taskWorkState.workResults$ : of(null))),
      )
      .subscribe((result) => {
        const wasEdited = result?.request === 'edit';
        if (result) {
          this.snackbarService.openSuccessSnack(
            wasEdited ? 'ACTIVITY_LOGGER.LOGGED_TASK.EDIT.SUCCESS' : 'ACTIVITY_LOGGER.LOGGED_TASK.CREATE.SUCCESS',
          );
          this.closeDrawer();
        } else {
          this.snackbarService.openErrorSnack(
            wasEdited ? 'ACTIVITY_LOGGER.LOGGED_TASK.EDIT.ERROR' : 'ACTIVITY_LOGGER.LOGGED_TASK.CREATE.ERROR',
          );
        }
      });

    const getActivity$ = this.activityId$$.asObservable().pipe(
      withLatestFrom(this.currentRowNamespace$$),
      switchMap(([activityId, namespace]) => {
        if (activityId) {
          return this.activityService.getMultiActivity({ crmObjectIds: [activityId], namespace });
        }
        return of({} as GetMultiActivityResponse);
      }),
      shareReplay(1),
    );
    this.activityLoadError$ = getActivity$.pipe(
      map(() => TaskErrorStatus.NO_ERROR),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 403) {
          return of(TaskErrorStatus.ACCESS_DENIED);
        }
        return of(TaskErrorStatus.UNKNOWN_ERROR);
      }),
      // prevent emitting multiple times when opening side panel
      debounceTime(100),
    );
    this.activity$ = getActivity$.pipe(
      map((response: GetMultiActivityResponse) => response?.activities?.[0]),
      shareReplay(1),
    );
    this.activity$.pipe(takeUntilDestroyed()).subscribe((activity) => {
      this.resetAssociations();
      if (activity?.associations?.length) {
        this.setAssociations(activity.associations);
      }
    });

    this.activityOwner$ = this.activity$.pipe(
      switchMap((activity) => {
        if (activity?.ownerId) {
          return this.config.services?.userService?.getMultiUsers([activity.ownerId]) ?? of([]);
        }
        return of([]);
      }),
      map((users) => this.mapUser(users?.[0])),
    );

    this.analyticsService.trackEvent('task-table-view', 'sales-tasks', 'view', 1);
  }

  private mapUser(user?: SimplifiedUser): UserWithFullName | null {
    if (user) {
      return {
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.displayName,
      } as UserWithFullName;
    }
    return null;
  }

  private isOverdue(row: Row): boolean {
    const dueDateField = row?.data[StandardIds.ActivityTaskDueDate];
    if (dueDateField?.cellType === 'custom') {
      const { dueDate, status } = this.getCustomCellFields(dueDateField as CustomCell);
      if (dueDate instanceof Date && status === ActivityTaskStatus.Open) {
        return dueDate.getTime() < Date.now();
      }
    } else if (dueDateField?.cellType === 'date' || dueDateField?.cellType === 'timestamp') {
      const dueDate = row?.data[StandardIds.ActivityTaskDueDate]?.value;
      const status = row?.data[StandardIds.ActivityTaskStatus]?.value;
      if (dueDate instanceof Date && status === ActivityTaskStatus.Open) {
        return dueDate.getTime() < Date.now();
      }
    }
    return false;
  }

  private isComplete(row: Row): boolean {
    const cell = row?.data[StandardIds.ActivityTaskStatus];
    if (cell?.cellType === 'custom') {
      const validFields: FieldValueInterface[] = cell.value?.filter(Boolean) ?? [];
      const statusField = validFields.find((field) => field.fieldId === StandardIds.ActivityTaskStatus);
      return statusField?.stringValue === ActivityTaskStatus.Completed;
    } else if (cell?.cellType === 'text') {
      const status = row?.data[StandardIds.ActivityTaskStatus]?.value;
      return status === ActivityTaskStatus.Completed;
    }
    return false;
  }

  private getCustomCellFields(cell: CustomCell): { dueDate?: Date; status?: string } {
    const validFields: FieldValueInterface[] = cell.value?.filter(Boolean) ?? [];
    const dueDateField = validFields.find((field) => field.fieldId === StandardIds.ActivityTaskDueDate);
    const statusField = validFields.find((field) => field.fieldId === StandardIds.ActivityTaskStatus);
    return {
      dueDate: dueDateField?.dateValue,
      status: statusField?.stringValue,
    };
  }

  private closeDrawer(): void {
    this.drawer?.close();
    this.currentRowNamespace$$.next('');
    this.activityId$$.next('');
  }

  private createActivity(
    fields: FieldValueInterface[],
    associations: ActivityAssociationInterface[],
    ownerId?: string,
  ): Observable<string> {
    const activity: ActivityInterface = {
      crmObjectSubtype: 'Task',
      fields,
      associations,
      ownerId,
    };
    return this.config.namespace$.pipe(
      switchMap((namespace) => this.activityService.createActivity({ activity, namespace })),
      map((response) => response.crmObjectId),
      tap((crmObjectId) => {
        activity.crmObjectId = crmObjectId;
        this.crmObjectManagementTableChangesService.addCreatedCrmObject(this.objectType, activity, this.activityType);
      }),
    );
  }

  private updateActivity(
    namespace: string,
    activityId: string,
    fields: FieldValueInterface[],
    oldFields: FieldValueInterface[],
    associations: ActivityAssociationInterface[],
    ownerId?: string,
  ): Observable<string> {
    const fieldMask = ['associations', ...this.fieldService.getFieldMaskFromFormGroup(this.taskLogger?.formGroup)];
    if (ownerId) {
      fieldMask.push('ownerId');
    }
    const activity: ActivityInterface = {
      crmObjectId: activityId,
      crmObjectSubtype: 'Task',
      fields,
      associations,
      ownerId,
      namespace: namespace,
    };

    return this.activityService.updateActivity({ activity, namespace, fieldMask: { paths: fieldMask } }).pipe(
      map(() => activityId),
      tap(() => {
        const fieldsCopy = [...fields];
        for (const oldField of oldFields) {
          if (fieldsCopy.some((field) => field.fieldId === oldField.fieldId)) {
            continue;
          }
          fieldsCopy.push(oldField);
        }
        activity.fields = fieldsCopy;
        this.crmObjectManagementTableChangesService.addUpdatedCrmObject(this.objectType, activity, this.activityType);
      }),
    );
  }

  private getAssociations(): ActivityAssociationInterface[] {
    const contacts = this.contactAssociations.map(
      (association) =>
        ({
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        }) as ActivityAssociationInterface,
    );
    const companies = this.companyAssociations.map(
      (association) =>
        ({
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        }) as ActivityAssociationInterface,
    );
    // Contact and Company associations are handled separately
    const filteredAssociations = this.otherAssociations.filter(
      (association) => !['Contact', 'Company'].includes(association.crmObjectType!),
    );
    const other = filteredAssociations.map(
      (association) =>
        ({
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        }) as ActivityAssociationInterface,
    );
    return [...contacts, ...companies, ...other];
  }

  private setAssociations(associations: ActivityAssociationInterface[]): void {
    const contactAssociations: FormFieldAssociation[] = [];
    const companyAssociations: FormFieldAssociation[] = [];
    const otherAssociations: FormFieldAssociation[] = [];
    associations.forEach((association) => {
      switch (association.crmObjectType) {
        case 'Contact':
          contactAssociations.push({
            crmObjectId: association.crmObjectId,
            crmObjectType: 'Contact',
          });
          break;
        case 'Company':
          companyAssociations.push({
            crmObjectId: association.crmObjectId,
            crmObjectType: 'Company',
          });
          break;
        default:
          otherAssociations.push({
            crmObjectId: association.crmObjectId,
            crmObjectType: association.crmObjectType as ObjectType | ObjectAssociationType,
          });
      }
    });
    this.contactAssociations = contactAssociations;
    this.companyAssociations = companyAssociations;
    this.otherAssociations = otherAssociations;
  }

  private resetAssociations(): void {
    this.contactAssociations = [];
    this.companyAssociations = [];
    this.otherAssociations = [];
  }

  private async addCreateTaskDefaultAssociations(): Promise<void> {
    const userId = await firstValueFrom(this.config.currentUserId$);
    this.otherAssociations.push({
      crmObjectId: userId,
      crmObjectType: 'User',
    });
  }

  handleCancelClick(): void {
    this.closeDrawer();
  }

  protected async handleCreateTaskClick(): Promise<void> {
    if (this.drawer && this.upsertTemplateRef) {
      this.resetAssociations();
      this.currentRowNamespace$$.next('');
      this.activityId$$.next('');
      await this.addCreateTaskDefaultAssociations();
      this.drawer.open(
        this.upsertTemplateRef,
        this.translateService.instant('TASK.LIST_OBJECTS_TABLE.ACTIONS.CREATE.TITLE'),
      );
    }
    this.analyticsService.trackEvent('task-table-create-task', 'sales-tasks', 'click', 1);
  }

  protected handleEditTaskClick(request: CRMRowObject): void {
    if (this.drawer && this.upsertTemplateRef) {
      this.resetAssociations();
      this.currentRowNamespace$$.next(request.namespace);
      this.activityId$$.next(request.objectId);
      this.drawer.open(
        this.upsertTemplateRef,
        this.translateService.instant('TASK.LIST_OBJECTS_TABLE.ACTIONS.EDIT.TITLE'),
      );
    }
    this.analyticsService.trackEvent('task-table-edit-task', 'sales-tasks', 'click', 1);
  }

  protected handleSaveClick(requestType: RequestType): void {
    const isEdit = requestType === 'edit';
    if (!this.taskLogger) {
      return;
    }

    this.taskLogger.validate();
    if (this.taskLogger.formGroup.invalid) {
      this.snackbarService.openErrorSnack('ACTIVITY_LOGGER.ERROR_SET_REQUIRED_FIELDS');
      return;
    }
    const fields = [...this.taskLogger.getValues(), manuallyLoggedFieldValue];
    const associations = this.getAssociations();
    const ownerId = this.taskLogger.ownerId;

    let work$: Observable<TaskResponse>;
    if (isEdit) {
      work$ = this.activity$.pipe(
        switchMap((activity) => {
          const activityId = activity?.crmObjectId;
          if (activityId) {
            return this.updateActivity(
              activity.namespace ?? '',
              activityId,
              fields,
              activity.fields ?? [],
              associations,
              ownerId,
            ).pipe(map((crmObjectId) => ({ crmObjectId, request: requestType }) as TaskResponse));
          }
          return of({ crmObjectId: activityId, request: requestType } as TaskResponse);
        }),
      );
    } else {
      work$ = this.createActivity(fields, associations, ownerId).pipe(
        map((crmObjectId) => ({ crmObjectId, request: requestType }) as TaskResponse),
      );
    }
    this.taskWorkState.startWork(work$);

    this.analyticsService.trackEvent('task-table-save-click', 'sales-tasks', 'click', 1, {
      requestType: requestType,
    });
  }

  protected handleRowsChange(value: Row[]): void {
    this.tableRows.set(value);
  }

  protected handleStartTaskQueueClick(): void {
    firstValueFrom(this.hasAccessToFeature$).then((hasAccess) => {
      if (hasAccess) {
        const incompleteTasks = this.tableRows().filter((task) => !this.isComplete(task));
        const validRows = incompleteTasks.filter((row) => row.id);

        firstValueFrom(this.isMultiLocation$).then((isMultiLocation) => {
          if (isMultiLocation) {
            const tasks: TaskWithNamespace[] = validRows.map((row) => {
              const namespace = row.data['namespace']?.value ?? '';
              return {
                id: row.id,
                namespace,
              };
            });

            this.verifyTaskPermissions(tasks).subscribe((results) => {
              const accessibleTaskIds = results.filter((result) => result.hasAccess).map((result) => result.id);

              const inaccessibleTasks = results.filter((result) => !result.hasAccess);

              if (accessibleTaskIds.length === 0) {
                this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.ERROR');
                return;
              }

              // If some tasks were removed due to lack of permission, show confirmation modal
              if (inaccessibleTasks.length > 0) {
                this.showConfirmationModal(inaccessibleTasks.length, tasks.length, () => {
                  this.startQueueWithAccessibleTasks(tasks, accessibleTaskIds);
                });
              } else {
                this.startQueueWithAccessibleTasks(tasks, accessibleTaskIds);
              }
            });
          } else {
            const tasks: TaskWithNamespace[] = validRows.map((row) => {
              const namespace = row.data['namespace']?.value ?? '';
              return {
                id: row.id,
                namespace,
              };
            });
            this.startTaskQueue(tasks.map((task) => task.id));
          }
        });
      } else {
        this.config.openRestrictedDialog?.('task-queue');
      }
    });
  }

  private startQueueWithAccessibleTasks(tasks: TaskWithNamespace[], accessibleTaskIds: string[]): void {
    const taskNamespaces = new Map<string, string>();
    accessibleTaskIds.forEach((id) => {
      const task = tasks.find((t) => t.id === id);
      if (task) {
        taskNamespaces.set(id, task.namespace);
      }
    });

    this.taskQueueService.setTaskNamespaces(taskNamespaces);
    this.taskQueueService.startQueue(accessibleTaskIds);

    this.analyticsService.trackEvent('task-table-start-queue', 'sales-tasks', 'click', 1, {
      numTasks: accessibleTaskIds.length,
      filteredTasks: tasks.length - accessibleTaskIds.length,
    });
  }

  private showConfirmationModal(inaccessibleCount: number, totalCount: number, onConfirm: () => void): void {
    import('./permission-confirmation-dialog.component').then(({ PermissionConfirmationDialogComponent }) => {
      const dialogRef = this.dialog.open(PermissionConfirmationDialogComponent, {
        width: '400px',
        data: {
          inaccessibleCount,
          totalCount,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          onConfirm();
        }
      });
    });
  }

  private startTaskQueue(taskIds: string[], defaultNamespace?: string): void {
    if (taskIds.length === 0) {
      this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.TASK_QUEUE.NO_TASKS_SELECTED');
      return;
    }
    const rows = this.tableRows();

    const validRows = taskIds
      .map((taskId) => rows.find((r) => r.id === taskId))
      .filter((row): row is Row => row !== null && row !== undefined);

    const tasks: TaskWithNamespace[] = validRows.map((row) => {
      const namespace = row.data['namespace']?.value ?? '';
      return {
        id: row.id,
        namespace,
      };
    });

    if (tasks.length === 0 && defaultNamespace) {
      this.taskQueueService.startQueue(taskIds, defaultNamespace);
      return;
    }

    firstValueFrom(this.isMultiLocation$).then((isMultiLocation) => {
      if (isMultiLocation) {
        const taskNamespaces = new Map<string, string>();
        tasks.forEach((task) => {
          taskNamespaces.set(task.id, task.namespace);
        });

        // Check permissions for each task
        this.verifyTaskPermissions(tasks).subscribe((results) => {
          const accessibleTaskIds = results.filter((result) => result.hasAccess).map((result) => result.id);

          const inaccessibleTasks = results.filter((result) => !result.hasAccess);

          // Update the namespace map to include only tasks with permission
          const filteredTaskNamespaces = new Map<string, string>();
          accessibleTaskIds.forEach((id) => {
            const namespace = taskNamespaces.get(id);
            if (namespace) {
              filteredTaskNamespaces.set(id, namespace);
            }
          });

          if (accessibleTaskIds.length === 0) {
            this.snackbarService.openErrorSnack('TASK.LIST_OBJECTS_TABLE.ACTIONS.COMPLETE.SINGLE.ERROR');
            return;
          }

          if (inaccessibleTasks.length > 0) {
            this.showConfirmationModal(inaccessibleTasks.length, tasks.length, () => {
              this.taskQueueService.setTaskNamespaces(filteredTaskNamespaces);
              this.taskQueueService.startQueue(accessibleTaskIds);

              this.analyticsService.trackEvent('task-table-start-queue', 'sales-tasks', 'click', 1, {
                numTasks: accessibleTaskIds.length,
                filteredTasks: tasks.length - accessibleTaskIds.length,
              });
            });
          } else {
            this.taskQueueService.setTaskNamespaces(filteredTaskNamespaces);
            this.taskQueueService.startQueue(accessibleTaskIds);

            this.analyticsService.trackEvent('task-table-start-queue', 'sales-tasks', 'click', 1, {
              numTasks: accessibleTaskIds.length,
              filteredTasks: tasks.length - accessibleTaskIds.length,
            });
          }
        });
      } else {
        const tasksByNamespace = new Map<string, string[]>();

        tasks.forEach((task) => {
          if (!tasksByNamespace.has(task.namespace)) {
            tasksByNamespace.set(task.namespace, []);
          }
          tasksByNamespace.get(task.namespace)?.push(task.id);
        });

        tasksByNamespace.forEach((ids, namespace) => {
          this.taskQueueService.startQueue(ids, namespace);
        });

        this.analyticsService.trackEvent('task-table-start-queue', 'sales-tasks', 'click', 1, {
          numTasks: tasks.length,
        });
      }
    });
  }

  private completeTaskMulti(tasks: TaskWithNamespace[]): Observable<string[]> {
    if (tasks.length === 0) {
      return of([]);
    }

    const completionObservables = tasks.map((task) => {
      return this.completeTask(task).pipe(map((success) => (success ? '' : task.id)));
    });

    return forkJoin(completionObservables).pipe(map((results) => results.filter(Boolean)));
  }

  private completeTask(task: TaskWithNamespace): Observable<boolean> {
    const activity: ActivityInterface = {
      crmObjectId: task.id,
      crmObjectSubtype: 'Task',
      fields: [
        {
          fieldId: StandardIds.ActivityTaskStatus,
          stringValue: ActivityTaskStatus.Completed,
        },
      ],
    };

    const fieldMask: FieldMaskInterface = {
      paths: [StandardIds.ActivityTaskStatus],
    };

    if (task.namespace) {
      return this.activityService
        .updateActivity({
          namespace: task.namespace,
          activity: activity,
          fieldMask: fieldMask,
        })
        .pipe(
          tap(() => {
            this.refreshRow$$.next(task.id);
          }),
          map(() => true),
          catchError(() => of(false)),
        );
    } else {
      return this.config.namespace$.pipe(
        take(1),
        switchMap((defaultNamespace) => {
          return this.activityService
            .updateActivity({
              namespace: defaultNamespace,
              activity: activity,
              fieldMask: fieldMask,
            })
            .pipe(
              tap(() => {
                this.refreshRow$$.next(task.id);
              }),
              map(() => true),
              catchError(() => of(false)),
            );
        }),
      );
    }
  }

  private verifyTaskPermissions(tasks: TaskWithNamespace[]): Observable<
    Array<{
      id: string;
      hasAccess: boolean;
    }>
  > {
    return forkJoin(
      tasks.map((task) =>
        this.activityService
          .getMultiActivity({
            crmObjectIds: [task.id],
            namespace: task.namespace,
          })
          .pipe(
            take(1),
            map(() => {
              return {
                id: task.id,
                hasAccess: true,
              };
            }),
            catchError((err: HttpErrorResponse) => {
              if (err.status === 403 || err.status === 404) {
                return of({
                  id: task.id,
                  hasAccess: false,
                });
              }
              return of({
                id: task.id,
                hasAccess: true,
              });
            }),
          ),
      ),
    );
  }
}
